'use client'

import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { SortingState } from '@tanstack/react-table'
import { ChevronLeft } from 'lucide-react'
import { toast } from 'sonner'

import { HeaderSection } from '@/components/shared/header-section'
import { PageContent } from '@/components/shared/page-content'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { DataTableManual } from '@/components/shared/data-table-manual/data-table-manual'
import { DataTableToolbar } from '@/components/shared/data-table-manual/data-table-toolbar-manual'

import { GET_ADDITIONAL_COMPONENT_GROUP } from './additionalComponentGroupQueries'
import {
  GET_GROUP_COMPONENTS,
  GET_GROUP_COMPONENTS_COUNT,
} from './additionalComponentQueries'
import { componentColumns, GroupComponent } from './components/componentColumns'

import useOpenDrawer from '@/hooks/useOpenDrawer'
import { useAdditionalComponentDelete } from './useAdditionalComponentDelete'
import { getOrderSortParams } from '@/utils/orderUtils'
import { useLanguage } from '@/context/LanguageContext'
import AdditionalComponentGroupForm from './AdditionalComponentGroupForm'
import AdditionalComponentEditForm from '@/containers/Products/components/AdditionalComponentEditForm'

export default function AdditionalComponentGroupDetails() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { language } = useLanguage()
  const groupId = parseInt(id || '0', 10)

  const [searchTerm, setSearchTerm] = useState('')
  const [tableData, setTableData] = useState<GroupComponent[]>([])
  const [tableDataCount, setTableDataCount] = useState(0)
  const [order, setOrder] = useState<SortingState>([])
  const [filters, setFilters] = useState<Map<string, Set<string>>>(new Map())

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  })

  // Fetch group header info
  const { data: groupData, loading: loadingGroup } = useQuery(
    GET_ADDITIONAL_COMPONENT_GROUP,
    {
      variables: { id: groupId },
      skip: !groupId,
    }
  )

  // Fetch paginated components in the group
  const [loadComponents, { data: componentsData, loading: loadingComponents }] =
    useLazyQuery(GET_GROUP_COMPONENTS, {
      fetchPolicy: 'cache-and-network',
    })

  // Fetch count separately
  const [loadCount, { data: countData }] = useLazyQuery(
    GET_GROUP_COMPONENTS_COUNT,
    {
      fetchPolicy: 'cache-and-network',
    }
  )

  useEffect(() => {
    if (!groupId) return
    loadComponents({
      variables: {
        groupId,
        searchTerm,
        take: pagination.pageSize,
        skip: pagination.pageIndex * pagination.pageSize,
        orderBy: getOrderSortParams(order),
      },
    })
    loadCount({
      variables: {
        groupId,
        searchTerm,
      },
    })
  }, [groupId, searchTerm, pagination, order])

  useEffect(() => {
    setPagination(prev => ({ ...prev, pageIndex: 0 }))
  }, [searchTerm, pagination.pageSize])

  useEffect(() => {
    if (componentsData) {
      setTableData(componentsData.findManyAdditionalComponent || [])
    }
  }, [componentsData])

  useEffect(() => {
    if (countData) {
      setTableDataCount(countData.findManyAdditionalComponentCount ?? 0)
    }
  }, [countData])

  // Guarded per-component delete. (The old generic hook sent `ids` to a
  // mutation that takes `$id`, so bulk delete here never worked.)
  const { confirmDelete } = useAdditionalComponentDelete('component')
  const onDelete = (rows: GroupComponent[]) =>
    confirmDelete(rows.map(row => row.id))

  const openEditGroupForm = useOpenDrawer(AdditionalComponentGroupForm)
  const openComponentForm = useOpenDrawer(AdditionalComponentEditForm)

  const group = groupData?.findUniqueAdditionalComponentGroup

  const handleEditGroup = () => {
    openEditGroupForm({ id: groupId })
  }

  const handleCreateComponent = () => {
    openComponentForm({
      id: undefined,
      componentGroupId: groupId,
      manufacturerId: group?.manufacturerId,
      componentGroup: group,
    })
  }

  const handleRowClick = (row: GroupComponent) => {
    openComponentForm({
      id: row.id,
      componentGroupId: groupId,
      manufacturerId: group?.manufacturerId,
      componentGroup: group,
    })
  }

  const groupName =
    group?.additional_component_group_profiles?.find(
      (p: any) => p.language === language
    )?.name ||
    group?.additional_component_group_profiles?.[0]?.name ||
    group?.code ||
    'Group'

  return (
    <PageContent>
      <div className="flex items-center gap-2 mb-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/products/additional-component-groups')}
          className="flex items-center gap-1 text-muted-foreground"
        >
          <ChevronLeft className="h-4 w-4" />
          Back
        </Button>
      </div>

      <div className="flex justify-between items-start mb-4">
        <div>
          <HeaderSection
            title={`Additional Component Group: ${loadingGroup ? '...' : groupName}`}
          />
          {group && (
            <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
              <span>
                Code:{' '}
                <span className="font-mono font-medium text-foreground">
                  {group.code || '—'}
                </span>
              </span>
              <span>
                Components:{' '}
                <Badge variant="secondary" className="text-xs">
                  {tableDataCount}
                </Badge>
              </span>
              <span>
                Fabric Prices:{' '}
                <Badge
                  variant={
                    group.use_fabric_prices_for_components
                      ? 'default'
                      : 'secondary'
                  }
                  className="text-xs"
                >
                  {group.use_fabric_prices_for_components ? 'Yes' : 'No'}
                </Badge>
              </span>
              <span>
                Custom Dims:{' '}
                <Badge
                  variant={
                    group.enable_custom_dimensions_for_components
                      ? 'default'
                      : 'secondary'
                  }
                  className="text-xs"
                >
                  {group.enable_custom_dimensions_for_components ? 'Yes' : 'No'}
                </Badge>
              </span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleEditGroup}>
            Edit Group
          </Button>
          <Button size="sm" onClick={handleCreateComponent}>
            + Create Component
          </Button>
        </div>
      </div>

      <DataTableManual<GroupComponent, unknown>
        columns={componentColumns}
        data={tableData}
        rowCount={tableDataCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        onPaginationChange={setPagination}
        onSortingChange={setOrder}
        onColumnFiltersChange={setFilters}
        onRowClick={handleRowClick}
        isLoading={loadingComponents}
        className="[&_tr:hover]:cursor-pointer"
        toolbar={(table: any) => (
          <DataTableToolbar
            table={table}
            config={{
              onDelete: onDelete,
              search: {
                placeholder: 'Search components...',
                accessor: 'name',
                onSearch: setSearchTerm,
                onFilter: setFilters,
              },
              columns: [],
            }}
          />
        )}
      />
    </PageContent>
  )
}
